/* eslint-disable */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

import styles from './styles/styles.module.css';

import { selectRedZoneRealtimeVersion } from '../../../../Redux/slices/redZoneSlice';

import {
  RED_ZONE_INCIDENT_TYPE_OPTIONS,
  RedZoneIncidentType,
} from '../../../../types/redzone.constants';

import {
  createRedZoneObservation,
  createRedZoneReport,
  deleteRedZoneObservation,
  getRedZoneObservationById,
  getRedZoneObservations,
  type RedZoneObservation,
  type RedZoneObservationClassification,
  type RedZoneObservationEntityType,
  type RedZonePerson,
  type RedZoneReportStatus,
  type RedZoneRiskLevel,
  type RedZoneTravelDirection,
  type RedZoneVehicle,
} from '../../../../api/redZone/post';

import RedZoneObservationMap, {
  type RedZoneDraftLocation,
} from './RedZoneObservationMap';

import {
  BsActivity,
  // BsArrowUpRightSquare,
  BsBookmark,
  BsCarFrontFill,
  BsChevronRight,
  BsClockHistory,
  BsConeStriped,
  BsExclamationTriangle,
  BsFunnel,
  BsGeoAltFill,
  BsPersonFill,
  BsSendFill,
  BsShieldFillExclamation,
  BsThreeDots,
  BsTrash3,
} from 'react-icons/bs';

type TimePreset =
  '1D' | '7D' | '14D' | '30D' | '60D' | '90D' | 'CUSTOM' | 'ALL';

type ProtectorOption = {
  id: string;
  name: string;
};

type ObservationComposer = {
  observedAt: string;
  protectorId: string;
  classification: RedZoneObservationClassification;
  direction: RedZoneTravelDirection | '';
  narrative: string;
  vehicle: RedZoneVehicle;
  person: RedZonePerson;
};

type FormalReportForm = {
  observationId: string;
  observedAt: string;
  protectorId: string;
  direction: RedZoneTravelDirection | '';
  incidentType: RedZoneIncidentType | '';
  riskLevel: RedZoneRiskLevel | '';
  narrative: string;
  vehicle: RedZoneVehicle;
  person: RedZonePerson;
  vehiclePhoto: File | null;
  personPhoto: File | null;
};

const ENTITY_OPTIONS: Array<{
  value: RedZoneObservationEntityType;
  label: string;
  icon: React.ReactNode;
}> = [
  { value: 'VEHICLE', label: 'Vehicle', icon: <BsCarFrontFill /> },
  { value: 'PERSON', label: 'Person', icon: <BsPersonFill /> },
  { value: 'ACTIVITY', label: 'Activity', icon: <BsActivity /> },
  { value: 'HAZARD', label: 'Hazard', icon: <BsConeStriped /> },
  { value: 'OTHER', label: 'Other', icon: <BsThreeDots /> },
];

const CLASSIFICATIONS: RedZoneObservationClassification[] = [
  'BASELINE',
  'UNUSUAL',
  'SUSPICIOUS',
  'THREAT',
];

const DIRECTIONS: RedZoneTravelDirection[] = [
  'NORTH',
  'SOUTH',
  'EAST',
  'WEST',
  'STATIONARY',
];

const RISK_LEVELS: RedZoneRiskLevel[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const TIME_PRESETS: TimePreset[] = [
  '1D',
  '7D',
  '14D',
  '30D',
  '60D',
  '90D',
  'CUSTOM',
  'ALL',
];

const VEHICLE_FIELDS: Array<{
  key: keyof RedZoneVehicle;
  label: string;
  wide?: boolean;
}> = [
  { key: 'licensePlate', label: 'License Plate' },
  { key: 'state', label: 'State' },
  { key: 'make', label: 'Make' },
  { key: 'model', label: 'Model' },
  { key: 'color', label: 'Color' },
  { key: 'bodyType', label: 'Body Type' },
  { key: 'occupants', label: 'Occupants' },
  { key: 'distinguishingMarks', label: 'Distinguishing Marks', wide: true },
];

const PERSON_FIELDS: Array<{
  key: keyof RedZonePerson;
  label: string;
  wide?: boolean;
}> = [
  { key: 'gender', label: 'Gender' },
  { key: 'approximateAge', label: 'Approx. Age' },
  { key: 'height', label: 'Height' },
  { key: 'build', label: 'Build' },
  { key: 'clothing', label: 'Clothing', wide: true },
  { key: 'hair', label: 'Hair / Facial Hair' },
  { key: 'identifyingFeatures', label: 'Identifying Features', wide: true },
  { key: 'behavior', label: 'Behavior', wide: true },
  { key: 'associatedVehicle', label: 'Associated Vehicle', wide: true },
];

function unwrapArray(value: any): any[] {
  if (Array.isArray(value)) return value;

  const candidates = [
    value?.sites,
    value?.users,
    value?.items,
    value?.data,
    value?.associatedSites,
    value?.associatedUsers,
  ];

  return candidates.find(Array.isArray) ?? [];
}

function getRecordId(value: any): string {
  return String(
    value?._id ?? value?.userId ?? value?.siteId ?? value?.id ?? ''
  );
}

function getUserLabel(user: any): string {
  const firstName = user?.firstName ?? user?.first ?? '';
  const lastName = user?.lastName ?? user?.last ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || user?.name || user?.email || 'Protector';
}

function getSiteAddress(site: any): string {
  const cityState = [site?.siteCity, site?.siteState]
    .filter(Boolean)
    .join(', ');

  return [site?.siteStreet, cityState, site?.siteZip].filter(Boolean).join(' ');
}

function emptyVehicle(): RedZoneVehicle {
  return {
    licensePlate: '',
    state: '',
    make: '',
    model: '',
    color: '',
    bodyType: '',
    occupants: '',
    distinguishingMarks: '',
  };
}

function emptyPerson(): RedZonePerson {
  return {
    gender: '',
    approximateAge: '',
    height: '',
    build: '',
    clothing: '',
    hair: '',
    identifyingFeatures: '',
    behavior: '',
    associatedVehicle: '',
  };
}

function localDateTimeValue(timezone: string) {
  return moment()
    .tz(timezone || 'America/Los_Angeles')
    .format('YYYY-MM-DDTHH:mm');
}

function localInputToIso(value: string, timezone: string) {
  if (!value) return undefined;

  const parsed = moment.tz(
    value,
    'YYYY-MM-DDTHH:mm',
    timezone || 'America/Los_Angeles'
  );

  return parsed.isValid() ? parsed.toISOString() : undefined;
}

function observationToLocalInput(value: string, timezone: string) {
  return moment(value)
    .tz(timezone || 'America/Los_Angeles')
    .format('YYYY-MM-DDTHH:mm');
}

function getDateBounds(
  preset: TimePreset,
  customFrom: string,
  customTo: string,
  timezone: string
): { from?: string; to?: string } {
  if (preset === 'ALL') return {};

  if (preset === 'CUSTOM') {
    const result: { from?: string; to?: string } = {};

    if (customFrom) {
      result.from = moment
        .tz(customFrom, 'YYYY-MM-DD', timezone)
        .startOf('day')
        .toISOString();
    }

    if (customTo) {
      result.to = moment
        .tz(customTo, 'YYYY-MM-DD', timezone)
        .endOf('day')
        .toISOString();
    }

    return result;
  }

  const days = Number(preset.replace('D', ''));
  const now = moment();

  return {
    from: now.clone().subtract(days, 'days').toISOString(),
    to: now.toISOString(),
  };
}

function formatObservationTime(value: string, timezone: string) {
  return moment(value).tz(timezone).format('MMM D, YYYY • h:mm A');
}

function getLocationLabel(observation: RedZoneObservation) {
  if (observation.location?.formattedAddress) {
    return observation.location.formattedAddress;
  }

  const coordinates = observation.location?.coordinates;

  if (!Array.isArray(coordinates) || coordinates.length !== 2) {
    return 'Map location';
  }

  return `${coordinates[1].toFixed(5)}, ${coordinates[0].toFixed(5)}`;
}

function incidentForEntity(
  entityType: RedZoneObservationEntityType
): RedZoneIncidentType {
  switch (entityType) {
    case 'VEHICLE':
      return 'SUSPICIOUS_VEHICLE';
    case 'PERSON':
      return 'SUSPICIOUS_PERSON';
    case 'ACTIVITY':
      return 'SUSPICIOUS_ACTIVITY';
    default:
      return 'OTHER';
  }
}

function hasMeaningfulValues(value: any) {
  if (!value || typeof value !== 'object') return false;

  return Object.values(value).some(
    (item) => item !== undefined && item !== null && String(item).trim() !== ''
  );
}

function classificationClass(classification: RedZoneObservationClassification) {
  switch (classification) {
    case 'BASELINE':
      return styles.classificationBaseline;
    case 'UNUSUAL':
      return styles.classificationUnusual;
    case 'SUSPICIOUS':
      return styles.classificationSuspicious;
    case 'THREAT':
      return styles.classificationThreat;
  }
}

const Field: React.FC<{
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ label, required, className, children }) => (
  <label className={[styles.field, className ?? ''].filter(Boolean).join(' ')}>
    <span>
      {label}
      {required && <b className={styles.required}>*</b>}
    </span>
    {children}
  </label>
);

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  danger?: boolean;
}> = ({ icon, title, subtitle, danger }) => (
  <div className={styles.sectionHeader}>
    <div className={danger ? styles.sectionDanger : styles.sectionIcon}>
      {icon}
    </div>
    <div>
      <h2 className={styles.sectionHeaderTitle}>{title}</h2>
      {subtitle && <p className={styles.sectionHeaderSubtitle}>{subtitle}</p>}
    </div>
  </div>
);

const UploadBox: React.FC<{
  label: string;
  file: File | null;
  onChange: (file: File | null) => void;
}> = ({ label, file, onChange }) => (
  <label className={styles.uploadBox}>
    <input
      type="file"
      accept="image/jpeg,image/png"
      onChange={(event) => onChange(event.target.files?.[0] ?? null)}
    />
    <span className={styles.uploadIcon}>+</span>
    <strong>{file ? file.name : label}</strong>
    <small>JPG or PNG, max 10 MB</small>
  </label>
);

const MAX_OBSERVATION_PHOTOS = 5;
const MAX_OBSERVATION_PHOTO_BYTES = 10 * 1024 * 1024;

const ObservationPhotoPicker: React.FC<{
  files: File[];
  required: boolean;
  onChange: (files: File[]) => void;
  onError: (message: string) => void;
}> = ({ files, required, onChange, onError }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const previews = useMemo(
    () =>
      files.map((file) => ({
        file,
        url: URL.createObjectURL(file),
      })),
    [files]
  );

  useEffect(
    () => () => {
      for (const preview of previews) {
        URL.revokeObjectURL(preview.url);
      }
    },
    [previews]
  );

  const addFiles = (selectedFiles: FileList | null) => {
    if (!selectedFiles?.length) return;

    const incoming = Array.from(selectedFiles);

    const invalidType = incoming.find(
      (file) => file.type !== 'image/jpeg' && file.type !== 'image/png'
    );

    if (invalidType) {
      onError('Only JPG and PNG images may be attached to an observation.');
      return;
    }

    const oversized = incoming.find(
      (file) => file.size > MAX_OBSERVATION_PHOTO_BYTES
    );

    if (oversized) {
      onError('Each observation photo must be 10 MB or smaller.');
      return;
    }

    const combined = [...files];

    for (const file of incoming) {
      const duplicate = combined.some(
        (existing) =>
          existing.name === file.name &&
          existing.size === file.size &&
          existing.lastModified === file.lastModified
      );

      if (!duplicate) {
        combined.push(file);
      }
    }

    if (combined.length > MAX_OBSERVATION_PHOTOS) {
      onError(
        `A maximum of ${MAX_OBSERVATION_PHOTOS} photos may be attached to an observation.`
      );
      return;
    }

    onError('');
    onChange(combined);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    onChange(files.filter((_, fileIndex) => fileIndex !== index));
  };

  return (
    <div className={styles.observationPhotoSection}>
      <div className={styles.observationPhotoHeader}>
        <div>
          <div className={styles.observationPhotoTitle}>
            Observation Photos
            {required && <b className={styles.required}>*</b>}
          </div>

          <div className={styles.observationPhotoHint}>
            {required
              ? 'At least one photo is required for suspicious or threat observations.'
              : 'Recommended for documentation. Photos are optional for baseline and unusual observations.'}
          </div>
        </div>

        <span className={styles.observationPhotoCount}>
          {files.length}/{MAX_OBSERVATION_PHOTOS}
        </span>
      </div>

      <div className={styles.observationPhotoGrid}>
        {previews.map(({ file, url }, index) => (
          <div
            key={`${file.name}-${file.size}-${file.lastModified}`}
            className={styles.observationPhotoPreview}
          >
            <img src={url} alt={`Observation evidence ${index + 1}`} />

            <button
              type="button"
              className={styles.observationPhotoRemove}
              onClick={() => removeFile(index)}
              aria-label={`Remove ${file.name}`}
            >
              ×
            </button>

            <div className={styles.observationPhotoName} title={file.name}>
              {file.name}
            </div>
          </div>
        ))}

        {files.length < MAX_OBSERVATION_PHOTOS && (
          <label className={styles.observationPhotoAdd}>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png"
              multiple
              onChange={(event) => addFiles(event.target.files)}
            />

            <span className={styles.observationPhotoAddIcon}>+</span>
            <strong>Add Photos</strong>
            <small>JPG or PNG • max 10 MB each</small>
          </label>
        )}
      </div>
    </div>
  );
};

const RedZone: React.FC = () => {
  const currentSite = useSelector((state: any) => state.currentSite ?? {});
  const allUsersState = useSelector((state: any) => state.allUsers);
  const currentUser = useSelector((state: any) => state.user ?? {});

  const allUsers = useMemo(() => unwrapArray(allUsersState), [allUsersState]);

  /* =======================================================
     CURRENT SITE
     =======================================================

     currentSite is hydrated from the backend's resolved
     selectedSite object and is now the authoritative source
     for the active site's name, timezone, shift start,
     and address.
     ======================================================= */

  const siteId = currentSite?.siteId ?? '';
  const siteName = currentSite?.siteName ?? '';

  const redZoneRealtimeVersion = useSelector((state: any) =>
    selectRedZoneRealtimeVersion(state, siteId)
  );

  const timezone = currentSite?.timezone || 'America/Los_Angeles';

  const siteAddress = useMemo(
    () => getSiteAddress(currentSite),
    [
      currentSite?.siteStreet,
      currentSite?.siteCity,
      currentSite?.siteState,
      currentSite?.siteZip,
    ]
  );

  const protectorOptions = useMemo<ProtectorOption[]>(() => {
    const map = new Map<string, ProtectorOption>();

    for (const user of allUsers) {
      const id = getRecordId(user);
      if (!id) continue;

      map.set(id, {
        id,
        name: getUserLabel(user),
      });
    }

    const currentId = getRecordId(currentUser);

    if (currentId && !map.has(currentId)) {
      map.set(currentId, {
        id: currentId,
        name: getUserLabel(currentUser),
      });
    }

    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [allUsers, currentUser]);

  const validSiteId = Boolean(
    siteId && siteId !== '0000' && siteName !== 'MASTER'
  );

  const mapApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const [timePreset, setTimePreset] = useState<TimePreset>('7D');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [protectorFilter, setProtectorFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState<
    RedZoneObservationEntityType | ''
  >('');
  const [classificationFilter, setClassificationFilter] = useState<
    RedZoneObservationClassification | ''
  >('');

  const [observations, setObservations] = useState<RedZoneObservation[]>([]);
  const [observationsLoading, setObservationsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedObservationId, setSelectedObservationId] = useState<
    string | null
  >(null);
  const [selectedObservationDetail, setSelectedObservationDetail] =
    useState<RedZoneObservation | null>(null);
  const [
    selectedObservationDetailLoading,
    setSelectedObservationDetailLoading,
  ] = useState(false);

  const [dropMode, setDropMode] = useState<RedZoneObservationEntityType | null>(
    null
  );
  const [draftLocation, setDraftLocation] =
    useState<RedZoneDraftLocation | null>(null);

  const makeComposer = useCallback(
    (): ObservationComposer => ({
      observedAt: localDateTimeValue(timezone),
      protectorId: getRecordId(currentUser),
      classification: 'BASELINE',
      direction: '',
      narrative: '',
      vehicle: emptyVehicle(),
      person: emptyPerson(),
    }),
    [timezone, currentUser]
  );

  const [composer, setComposer] = useState<ObservationComposer>(() =>
    makeComposer()
  );
  const [savingObservation, setSavingObservation] = useState(false);
  const [observationPhotos, setObservationPhotos] = useState<File[]>([]);
  const [deletingObservation, setDeletingObservation] = useState(false);
  const [formalReport, setFormalReport] = useState<FormalReportForm | null>(
    null
  );
  const [savingReport, setSavingReport] = useState(false);

  const requestVersion = useRef(0);

  const previousRealtimeVersion = useRef(redZoneRealtimeVersion);

  const previousRealtimeSiteId = useRef(siteId);

  const selectedObservation = useMemo(() => {
    if (
      selectedObservationDetail &&
      selectedObservationDetail._id === selectedObservationId
    ) {
      return selectedObservationDetail;
    }

    return (
      observations.find(
        (observation) => observation._id === selectedObservationId
      ) ?? null
    );
  }, [observations, selectedObservationId, selectedObservationDetail]);

  const observationPhotoRequired =
    composer.classification === 'SUSPICIOUS' ||
    composer.classification === 'THREAT';

  const loadObservations = useCallback(async () => {
    if (!validSiteId) {
      setObservations([]);
      return;
    }

    const requestId = ++requestVersion.current;
    setObservationsLoading(true);
    setErrorMessage('');

    try {
      const bounds = getDateBounds(timePreset, customFrom, customTo, timezone);

      const response = await getRedZoneObservations({
        siteId,
        ...bounds,
        protectorId: protectorFilter || undefined,
        entityType: entityFilter || undefined,
        classification: classificationFilter || undefined,
      });

      if (requestId !== requestVersion.current) return;

      if (!response.success) {
        throw new Error(response.msg || 'Unable to load observations.');
      }

      setObservations(response.data ?? []);
    } catch (error: any) {
      if (requestId !== requestVersion.current) return;

      console.error('Red Zone observation load failed:', error);
      setErrorMessage(
        error?.response?.data?.msg ||
          error?.message ||
          'Unable to load Red Zone observations.'
      );
    } finally {
      if (requestId === requestVersion.current) {
        setObservationsLoading(false);
      }
    }
  }, [
    validSiteId,
    siteId,
    timePreset,
    customFrom,
    customTo,
    timezone,
    protectorFilter,
    entityFilter,
    classificationFilter,
  ]);

  useEffect(() => {
    void loadObservations();
  }, [loadObservations]);

  /*
   * =======================================================
   * RED ZONE REALTIME REFRESH
   * =======================================================
   *
   * The socket bridge receives "redZone:update" and increments
   * this site's Redux realtime version. We then refetch the
   * authoritative observation data instead of reconstructing
   * observations from the lightweight socket payload.
   * =======================================================
   */
  useEffect(() => {
    /*
     * A site switch already causes loadObservations() to run
     * through its dependency list. Reset the checkpoint so the
     * previous site's realtime version does not trigger a second
     * request for the newly selected site.
     */
    if (previousRealtimeSiteId.current !== siteId) {
      previousRealtimeSiteId.current = siteId;
      previousRealtimeVersion.current = redZoneRealtimeVersion;

      return;
    }

    /*
     * Do nothing on the initial render. Only react when the
     * socket bridge actually increments this site's version.
     */
    if (previousRealtimeVersion.current === redZoneRealtimeVersion) {
      return;
    }

    previousRealtimeVersion.current = redZoneRealtimeVersion;

    if (!validSiteId) {
      return;
    }

    console.log('🔄 Red Zone realtime update - refreshing web site:', siteId);

    void loadObservations();
  }, [redZoneRealtimeVersion, siteId, validSiteId, loadObservations]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedObservationId) {
      setSelectedObservationDetail(null);
      setSelectedObservationDetailLoading(false);
      return;
    }

    const loadSelectedObservation = async () => {
      setSelectedObservationDetailLoading(true);

      try {
        const response = await getRedZoneObservationById(selectedObservationId);

        if (cancelled) return;

        if (!response.success || !response.data) {
          throw new Error(
            response.msg || 'Unable to load observation details.'
          );
        }

        setSelectedObservationDetail(response.data);
      } catch (error: any) {
        if (cancelled) return;

        console.error('Red Zone observation detail load failed:', error);

        /*
         * Keep the lightweight list/map copy visible if detail hydration
         * fails. The user can still inspect the record metadata.
         */
        setSelectedObservationDetail(null);

        setErrorMessage(
          error?.response?.data?.msg ||
            error?.message ||
            'Unable to load observation details.'
        );
      } finally {
        if (!cancelled) {
          setSelectedObservationDetailLoading(false);
        }
      }
    };

    void loadSelectedObservation();

    return () => {
      cancelled = true;
    };
  }, [selectedObservationId, redZoneRealtimeVersion]);

  useEffect(() => {
    setSelectedObservationId(null);
    setSelectedObservationDetail(null);
    setDropMode(null);
    setDraftLocation(null);
    setFormalReport(null);
    setComposer(makeComposer());
    setObservationPhotos([]);
  }, [siteId, makeComposer]);

  const startDrop = (entityType: RedZoneObservationEntityType) => {
    setSuccessMessage('');
    setErrorMessage('');
    setSelectedObservationId(null);
    setSelectedObservationDetail(null);
    setFormalReport(null);
    setDraftLocation(null);
    setDropMode(entityType);
    setComposer(makeComposer());
    setObservationPhotos([]);
  };

  const cancelDrop = () => {
    setDropMode(null);
    setDraftLocation(null);
    setObservationPhotos([]);
  };

  const selectedProtectorName = protectorOptions.find(
    (protector) => protector.id === composer.protectorId
  )?.name;

  const saveObservation = async () => {
    if (!validSiteId || !dropMode || !draftLocation) return;

    if (observationPhotoRequired && observationPhotos.length === 0) {
      setErrorMessage(
        'At least one photo is required for suspicious or threat observations.'
      );
      return;
    }

    if (observationPhotos.length > MAX_OBSERVATION_PHOTOS) {
      setErrorMessage(
        `A maximum of ${MAX_OBSERVATION_PHOTOS} photos may be attached to an observation.`
      );
      return;
    }

    setSavingObservation(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const payload: any = {
        siteId,
        observedAt: localInputToIso(composer.observedAt, timezone),
        protectorId: composer.protectorId || undefined,
        protectorNameSnapshot: selectedProtectorName,
        location: {
          coordinates: draftLocation.coordinates,
          formattedAddress: draftLocation.formattedAddress,
          placeId: draftLocation.placeId,
          source: draftLocation.source,
        },
        entityType: dropMode,
        classification: composer.classification,
        direction: composer.direction || undefined,
        narrative: composer.narrative.trim() || undefined,
      };

      if (dropMode === 'VEHICLE' && hasMeaningfulValues(composer.vehicle)) {
        payload.vehicle = composer.vehicle;
      }

      if (dropMode === 'PERSON' && hasMeaningfulValues(composer.person)) {
        payload.person = composer.person;
      }

      const response = await createRedZoneObservation(
        payload,
        observationPhotos
      );

      if (!response.success || !response.data) {
        throw new Error(response.msg || 'Unable to save observation.');
      }

      const createdId = response.data._id;
      setDropMode(null);
      setDraftLocation(null);
      setObservationPhotos([]);
      setSuccessMessage('Observation saved.');

      await loadObservations();
      setSelectedObservationId(createdId);
    } catch (error: any) {
      console.error('Red Zone observation save failed:', error);
      setErrorMessage(
        error?.response?.data?.msg ||
          error?.message ||
          'Unable to save observation.'
      );
    } finally {
      setSavingObservation(false);
    }
  };

  const selectObservation = (observation: RedZoneObservation) => {
    setSelectedObservationDetail(null);
    setSelectedObservationId(observation._id);
    setDropMode(null);
    setDraftLocation(null);
    setFormalReport(null);
    setSuccessMessage('');
  };

  const deleteSelectedObservation = async () => {
    if (!selectedObservation || selectedObservation.reportId) return;

    if (
      !window.confirm(
        'Delete this Red Zone observation? This cannot be undone.'
      )
    ) {
      return;
    }

    setDeletingObservation(true);
    setErrorMessage('');

    try {
      const response = await deleteRedZoneObservation(selectedObservation._id);

      if (!response.success) {
        throw new Error(response.msg || 'Unable to delete observation.');
      }

      setSelectedObservationId(null);
      setSelectedObservationDetail(null);
      setSuccessMessage('Observation deleted.');
      await loadObservations();
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.msg ||
          error?.message ||
          'Unable to delete observation.'
      );
    } finally {
      setDeletingObservation(false);
    }
  };

  // const prepareFormalReport = (observation: RedZoneObservation) => {
  //   setDropMode(null);
  //   setDraftLocation(null);
  //   setSelectedObservationId(observation._id);
  //   setFormalReport({
  //     observationId: observation._id,
  //     observedAt: observationToLocalInput(observation.observedAt, timezone),
  //     protectorId: observation.protectorId || '',
  //     direction: observation.direction || '',
  //     incidentType:
  //       (observation.incidentType as RedZoneIncidentType | undefined) ||
  //       incidentForEntity(observation.entityType),
  //     riskLevel: observation.riskLevel || '',
  //     narrative: observation.narrative || '',
  //     vehicle: observation.vehicle
  //       ? { ...observation.vehicle }
  //       : emptyVehicle(),
  //     person: observation.person ? { ...observation.person } : emptyPerson(),
  //     vehiclePhoto: null,
  //     personPhoto: null,
  //   });

  //   window.setTimeout(() => {
  //     document
  //       .getElementById('formal-red-zone-report')
  //       ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  //   }, 30);
  // };

  const submitFormalReport = async (status: RedZoneReportStatus) => {
    if (!formalReport || !selectedObservation) return;

    if (
      status !== 'DRAFT' &&
      (!formalReport.protectorId ||
        !formalReport.incidentType ||
        !formalReport.riskLevel ||
        !formalReport.narrative.trim())
    ) {
      setErrorMessage(
        'Protector, incident type, risk level and narrative are required before submitting or escalating a formal report.'
      );
      return;
    }

    setSavingReport(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const vehicleInvolved =
        selectedObservation.entityType === 'VEHICLE' ||
        hasMeaningfulValues(formalReport.vehicle) ||
        Boolean(formalReport.vehiclePhoto);

      const personInvolved =
        selectedObservation.entityType === 'PERSON' ||
        hasMeaningfulValues(formalReport.person) ||
        Boolean(formalReport.personPhoto);

      const response = await createRedZoneReport(
        {
          siteId,
          observationId: formalReport.observationId,
          observedAt: localInputToIso(formalReport.observedAt, timezone),
          protectorId: formalReport.protectorId || undefined,
          direction: formalReport.direction || undefined,
          incidentType: formalReport.incidentType || undefined,
          riskLevel: formalReport.riskLevel || undefined,
          status,
          narrative: formalReport.narrative.trim() || undefined,
          entities: {
            vehicle: vehicleInvolved,
            person: personInvolved,
          },
          vehicle: vehicleInvolved ? formalReport.vehicle : undefined,
          person: personInvolved ? formalReport.person : undefined,
        },
        {
          vehiclePhoto: formalReport.vehiclePhoto,
          personPhoto: formalReport.personPhoto,
        }
      );

      if (!response.success) {
        throw new Error(response.msg || 'Unable to create report.');
      }

      setFormalReport(null);
      setSuccessMessage(
        status === 'DRAFT'
          ? 'Formal report draft saved.'
          : status === 'ESCALATED'
            ? 'Formal report escalated.'
            : 'Formal report submitted.'
      );
      await loadObservations();
    } catch (error: any) {
      console.error('Formal Red Zone report save failed:', error);
      setErrorMessage(
        error?.response?.data?.msg ||
          error?.message ||
          'Unable to create Red Zone report.'
      );
    } finally {
      setSavingReport(false);
    }
  };

  const clearFilters = () => {
    setTimePreset('7D');
    setCustomFrom('');
    setCustomTo('');
    setProtectorFilter('');
    setEntityFilter('');
    setClassificationFilter('');
  };

  const updateComposerVehicle = (key: keyof RedZoneVehicle, value: string) => {
    setComposer((previous) => ({
      ...previous,
      vehicle: {
        ...previous.vehicle,
        [key]: value,
      },
    }));
  };

  const updateComposerPerson = (key: keyof RedZonePerson, value: string) => {
    setComposer((previous) => ({
      ...previous,
      person: {
        ...previous.person,
        [key]: value,
      },
    }));
  };

  const updateFormalVehicle = (key: keyof RedZoneVehicle, value: string) => {
    setFormalReport((previous) =>
      previous
        ? {
            ...previous,
            vehicle: {
              ...previous.vehicle,
              [key]: value,
            },
          }
        : previous
    );
  };

  const updateFormalPerson = (key: keyof RedZonePerson, value: string) => {
    setFormalReport((previous) =>
      previous
        ? {
            ...previous,
            person: {
              ...previous.person,
              [key]: value,
            },
          }
        : previous
    );
  };

  if (!validSiteId) {
    return (
      <div className={styles.page}>
        <div className={styles.noSiteState}>
          <BsShieldFillExclamation />
          <h1>Select a site</h1>
          <p>
            Red Zone observations are site-specific. Select a client site before
            opening the report.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerTitleGroup}>
          <div className={styles.headerIcon}>
            <BsShieldFillExclamation />
          </div>

          <div className={styles.titleCopy}>
            <div className={styles.headerTitleRow}>
              <h1 className={styles.headerTitle}>Red Zone Report</h1>
              <span className={styles.sitePill}>{siteName}</span>
            </div>
            <p className={styles.headerSubtitle}>
              Document and review observations around the protected site.
            </p>
          </div>
        </div>

        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => startDrop('VEHICLE')}
          >
            + New Observation
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() =>
              document
                .getElementById('red-zone-history')
                ?.scrollIntoView({ behavior: 'smooth' })
            }
          >
            <BsClockHistory />
            View History
          </button>
        </div>
      </div>

      <section className={styles.filterCard}>
        <div className={styles.filterTop}>
          <div className={styles.filterTitle}>
            <BsFunnel /> Observation History
          </div>
          <button
            type="button"
            className={styles.clearFiltersButton}
            onClick={clearFilters}
          >
            Clear Filters
          </button>
        </div>

        <div className={styles.timePresetRow}>
          {TIME_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={[
                styles.timePresetButton,
                timePreset === preset ? styles.timePresetButtonActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setTimePreset(preset)}
            >
              {preset === 'ALL'
                ? 'All Time'
                : preset === 'CUSTOM'
                  ? 'Custom'
                  : preset}
            </button>
          ))}
        </div>

        {timePreset === 'CUSTOM' && (
          <div className={styles.customDateRow}>
            <Field label="From">
              <input
                type="date"
                value={customFrom}
                onChange={(event) => setCustomFrom(event.target.value)}
              />
            </Field>
            <Field label="To">
              <input
                type="date"
                value={customTo}
                onChange={(event) => setCustomTo(event.target.value)}
              />
            </Field>
          </div>
        )}

        <div className={styles.filters}>
          <Field label="Protector">
            <select
              value={protectorFilter}
              onChange={(event) => setProtectorFilter(event.target.value)}
            >
              <option value="">All Protectors</option>
              {protectorOptions.map((protector) => (
                <option key={protector.id} value={protector.id}>
                  {protector.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Observation Type">
            <select
              value={entityFilter}
              onChange={(event) =>
                setEntityFilter(
                  event.target.value as RedZoneObservationEntityType | ''
                )
              }
            >
              <option value="">All Types</option>
              {ENTITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Classification">
            <select
              value={classificationFilter}
              onChange={(event) =>
                setClassificationFilter(
                  event.target.value as RedZoneObservationClassification | ''
                )
              }
            >
              <option value="">All Classifications</option>
              {CLASSIFICATIONS.map((classification) => (
                <option key={classification} value={classification}>
                  {classification}
                </option>
              ))}
            </select>
          </Field>

          <div className={styles.filterStat}>
            <span>Showing</span>
            <strong>{observations.length}</strong>
            <small>observations</small>
          </div>
        </div>
      </section>

      {errorMessage && (
        <div className={styles.errorBanner}>
          <BsExclamationTriangle />
          <span>{errorMessage}</span>
        </div>
      )}

      {successMessage && (
        <div className={styles.successBanner}>{successMessage}</div>
      )}

      <section className={styles.mapCard}>
        <div className={styles.mapCardHeader}>
          <div>
            <div className={styles.mapTitle}>
              <BsGeoAltFill /> Observation Map
            </div>
            <p className={styles.mapSubtitle}>
              Select what you observed, then click its approximate location on
              the map.
            </p>
          </div>

          {observationsLoading && (
            <div className={styles.mapLoadingRow}>Loading observations…</div>
          )}
        </div>

        <div className={styles.observationToolbar}>
          {ENTITY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={[
                styles.dropTypeButton,
                dropMode === option.value ? styles.dropTypeButtonActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => startDrop(option.value)}
            >
              {option.icon}
              <span>{option.label}</span>
            </button>
          ))}

          {dropMode && (
            <button
              type="button"
              className={styles.cancelMapDropButton}
              onClick={cancelDrop}
            >
              Cancel Drop
            </button>
          )}
        </div>

        <RedZoneObservationMap
          apiKey={mapApiKey}
          siteAddress={siteAddress}
          siteName={siteName}
          observations={observations}
          observationsLoading={observationsLoading}
          draftLocation={draftLocation}
          dropMode={dropMode}
          selectedObservationId={selectedObservationId}
          onMapDrop={(location) => {
            setDraftLocation(location);
            setSelectedObservationId(null);
          }}
          onObservationSelect={selectObservation}
        />
      </section>

      {dropMode && draftLocation && (
        <section className={styles.observationComposer}>
          <div className={styles.composerHeader}>
            <div>
              <div className={styles.composerEyebrow}>Unsaved Map Drop</div>
              <h2 className={styles.composerTitle}>
                New {dropMode.toLowerCase()} observation
              </h2>
              <p className={styles.composerSubtitle}>
                Baseline observations can be saved quickly. Add details only
                when they are useful.
              </p>
            </div>

            <span className={styles.locationChip}>
              <BsGeoAltFill />
              {draftLocation.formattedAddress ||
                `${draftLocation.coordinates[1].toFixed(5)}, ${draftLocation.coordinates[0].toFixed(5)}`}
            </span>
          </div>

          <div className={styles.composerGrid}>
            <Field label="Observed At">
              <input
                type="datetime-local"
                value={composer.observedAt}
                onChange={(event) =>
                  setComposer((previous) => ({
                    ...previous,
                    observedAt: event.target.value,
                  }))
                }
              />
            </Field>

            <Field label="Protector">
              <select
                value={composer.protectorId}
                onChange={(event) =>
                  setComposer((previous) => ({
                    ...previous,
                    protectorId: event.target.value,
                  }))
                }
              >
                <option value="">Current user / unknown</option>
                {protectorOptions.map((protector) => (
                  <option key={protector.id} value={protector.id}>
                    {protector.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Classification" required>
              <select
                value={composer.classification}
                onChange={(event) =>
                  setComposer((previous) => ({
                    ...previous,
                    classification: event.target
                      .value as RedZoneObservationClassification,
                  }))
                }
              >
                {CLASSIFICATIONS.map((classification) => (
                  <option key={classification} value={classification}>
                    {classification}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Direction">
              <select
                value={composer.direction}
                onChange={(event) =>
                  setComposer((previous) => ({
                    ...previous,
                    direction: event.target.value as
                      RedZoneTravelDirection | '',
                  }))
                }
              >
                <option value="">Not recorded</option>
                {DIRECTIONS.map((direction) => (
                  <option key={direction} value={direction}>
                    {direction}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Notes / Observation" className={styles.composerFull}>
              <textarea
                rows={3}
                value={composer.narrative}
                placeholder="Optional for baseline. Record behavior, movement, distinguishing details, or context."
                onChange={(event) =>
                  setComposer((previous) => ({
                    ...previous,
                    narrative: event.target.value,
                  }))
                }
              />
            </Field>
          </div>

          {dropMode === 'VEHICLE' && (
            <div className={styles.quickDetailsSection}>
              <h3>Vehicle Details</h3>
              <div className={styles.vehicleGrid}>
                {VEHICLE_FIELDS.map((field) => (
                  <Field
                    key={field.key}
                    label={field.label}
                    className={field.wide ? styles.spanTwo : undefined}
                  >
                    <input
                      value={String(composer.vehicle[field.key] ?? '')}
                      onChange={(event) =>
                        updateComposerVehicle(field.key, event.target.value)
                      }
                    />
                  </Field>
                ))}
              </div>
            </div>
          )}

          {dropMode === 'PERSON' && (
            <div className={styles.quickDetailsSection}>
              <h3>Person Details</h3>
              <div className={styles.personGrid}>
                {PERSON_FIELDS.map((field) => (
                  <Field
                    key={field.key}
                    label={field.label}
                    className={field.wide ? styles.spanTwo : undefined}
                  >
                    <input
                      value={String(composer.person[field.key] ?? '')}
                      onChange={(event) =>
                        updateComposerPerson(field.key, event.target.value)
                      }
                    />
                  </Field>
                ))}
              </div>
            </div>
          )}

          <ObservationPhotoPicker
            files={observationPhotos}
            required={observationPhotoRequired}
            onChange={setObservationPhotos}
            onError={setErrorMessage}
          />

          <div className={styles.composerActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={cancelDrop}
              disabled={savingObservation}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.saveObservationButton}
              onClick={saveObservation}
              disabled={
                savingObservation ||
                (observationPhotoRequired && observationPhotos.length === 0)
              }
              title={
                observationPhotoRequired && observationPhotos.length === 0
                  ? 'Add at least one photo before saving this observation.'
                  : undefined
              }
            >
              {savingObservation
                ? 'Saving…'
                : observationPhotoRequired && observationPhotos.length === 0
                  ? 'Add Photo to Save'
                  : 'Save Observation'}
            </button>
          </div>
        </section>
      )}

      <div id="red-zone-history" className={styles.bottomHoldingContainer}>
        <section className={styles.bottomLeftContainer}>
          <SectionHeader
            icon={<BsGeoAltFill />}
            title={
              selectedObservation
                ? 'Selected Observation'
                : 'Observation Details'
            }
            subtitle={
              selectedObservation
                ? 'Review the selected map record or turn it into a formal report.'
                : 'Click a map marker or choose an item from Recent Observations.'
            }
          />

          {selectedObservation ? (
            <div className={styles.selectedObservationCard}>
              <div className={styles.observationTitleLine}>
                <span className={styles.entityBadge}>
                  {selectedObservation.entityType}
                </span>
                <span
                  className={`${styles.classificationBadge} ${classificationClass(
                    selectedObservation.classification
                  )}`}
                >
                  {selectedObservation.classification}
                </span>
                {selectedObservation.reportId && (
                  <span className={styles.reportLinkBadge}>Report Linked</span>
                )}
              </div>

              <h3 className={styles.selectedLocationTitle}>
                {getLocationLabel(selectedObservation)}
              </h3>
              <p className={styles.selectedTime}>
                {formatObservationTime(
                  selectedObservation.observedAt,
                  timezone
                )}
              </p>

              <div className={styles.observationMetaGrid}>
                <div className={styles.observationMetaItem}>
                  <span className={styles.observationMetaLabel}>Protector</span>
                  <strong className={styles.observationMetaValue}>
                    {selectedObservation.protectorNameSnapshot ||
                      protectorOptions.find(
                        (protector) =>
                          protector.id === selectedObservation.protectorId
                      )?.name ||
                      'Not recorded'}
                  </strong>
                </div>
                <div className={styles.observationMetaItem}>
                  <span className={styles.observationMetaLabel}>Direction</span>
                  <strong className={styles.observationMetaValue}>
                    {selectedObservation.direction || 'Not recorded'}
                  </strong>
                </div>
                <div className={styles.observationMetaItem}>
                  <span className={styles.observationMetaLabel}>
                    Coordinates
                  </span>
                  <strong className={styles.observationMetaValue}>
                    {selectedObservation.location.coordinates[1].toFixed(5)},{' '}
                    {selectedObservation.location.coordinates[0].toFixed(5)}
                  </strong>
                </div>
              </div>

              {selectedObservation.narrative && (
                <div className={styles.observationText}>
                  {selectedObservation.narrative}
                </div>
              )}

              {selectedObservationDetailLoading && (
                <div
                  className={styles.observationEvidenceLoading}
                  role="status"
                  aria-live="polite"
                >
                  Loading observation evidence…
                </div>
              )}

              {!selectedObservationDetailLoading &&
                selectedObservation.attachments?.some((attachment) =>
                  Boolean(attachment.url)
                ) && (
                  <div className={styles.observationEvidenceSection}>
                    <div className={styles.observationEvidenceHeader}>
                      <div>
                        <strong>Observation Evidence</strong>
                        <span>
                          Photos captured with the original map observation.
                        </span>
                      </div>

                      <span className={styles.observationEvidenceCount}>
                        {
                          selectedObservation.attachments.filter((attachment) =>
                            Boolean(attachment.url)
                          ).length
                        }{' '}
                        photo
                        {selectedObservation.attachments.filter((attachment) =>
                          Boolean(attachment.url)
                        ).length === 1
                          ? ''
                          : 's'}
                      </span>
                    </div>

                    <div className={styles.observationEvidenceGrid}>
                      {selectedObservation.attachments
                        .filter((attachment) => Boolean(attachment.url))
                        .map((attachment, index) => (
                          <a
                            key={
                              attachment._id ||
                              attachment.storageKey ||
                              `${attachment.fileName ?? 'photo'}-${index}`
                            }
                            className={styles.observationEvidenceItem}
                            href={attachment.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img
                              src={attachment.url}
                              alt={
                                attachment.fileName ||
                                `Observation evidence ${index + 1}`
                              }
                            />

                            <div className={styles.observationEvidenceCaption}>
                              {attachment.fileName ||
                                `Observation photo ${index + 1}`}
                            </div>
                          </a>
                        ))}
                    </div>
                  </div>
                )}

              {selectedObservation.vehicle &&
                hasMeaningfulValues(selectedObservation.vehicle) && (
                  <div className={styles.summaryStrip}>
                    <BsCarFrontFill />
                    <span>
                      {[
                        selectedObservation.vehicle.color,
                        selectedObservation.vehicle.make,
                        selectedObservation.vehicle.model,
                        selectedObservation.vehicle.licensePlate
                          ? `Plate ${selectedObservation.vehicle.licensePlate}`
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' • ') || 'Vehicle details recorded'}
                    </span>
                  </div>
                )}

              {selectedObservation.person &&
                hasMeaningfulValues(selectedObservation.person) && (
                  <div className={styles.summaryStrip}>
                    <BsPersonFill />
                    <span>
                      {[
                        selectedObservation.person.gender,
                        selectedObservation.person.approximateAge
                          ? `Approx. ${selectedObservation.person.approximateAge}`
                          : '',
                        selectedObservation.person.clothing,
                      ]
                        .filter(Boolean)
                        .join(' • ') || 'Person details recorded'}
                    </span>
                  </div>
                )}

              <div className={styles.selectedObservationActions}>
                {!selectedObservation.reportId ? (
                  <>
                    {/* <button
                      type="button"
                      className={styles.createReportButton}
                      onClick={() => prepareFormalReport(selectedObservation)}
                    >
                      <BsArrowUpRightSquare /> Create Formal Report
                    </button> */}
                    <button
                      type="button"
                      className={styles.dangerButton}
                      onClick={deleteSelectedObservation}
                      disabled={deletingObservation}
                    >
                      <BsTrash3 />
                      {deletingObservation ? 'Deleting…' : 'Delete Observation'}
                    </button>
                  </>
                ) : (
                  <div className={styles.linkedReportNotice}>
                    This observation is linked to formal report{' '}
                    <strong>{selectedObservation.reportId}</strong>.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <BsGeoAltFill />
              <h3>No observation selected</h3>
              <p>Select a marker to inspect the historical record.</p>
            </div>
          )}
        </section>

        <aside className={styles.bottomRightContainer}>
          <SectionHeader
            icon={<BsClockHistory />}
            title="Recent Observations"
            subtitle="Newest records matching the active filters."
          />

          <div className={styles.recentObservations}>
            {observations.length === 0 ? (
              <div className={styles.emptyRecent}>
                No observations in this range.
              </div>
            ) : (
              observations.slice(0, 10).map((observation) => (
                <button
                  key={observation._id}
                  type="button"
                  className={[
                    styles.recentObservationCard,
                    selectedObservationId === observation._id
                      ? styles.recentObservationCardActive
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => selectObservation(observation)}
                >
                  <div className={styles.recentObservationTop}>
                    <strong className={styles.recentObservationTitle}>
                      {observation.entityType}
                    </strong>
                    <BsChevronRight />
                  </div>
                  <div className={styles.recentObservationMeta}>
                    {formatObservationTime(observation.observedAt, timezone)}
                  </div>
                  <div className={styles.recentObservationLocation}>
                    {getLocationLabel(observation)}
                  </div>
                  <div className={styles.recentObservationBadges}>
                    <span
                      className={`${styles.classificationBadge} ${classificationClass(
                        observation.classification
                      )}`}
                    >
                      {observation.classification}
                    </span>
                    {observation.reportId && (
                      <span className={styles.reportLinkBadge}>Report</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>
      </div>

      {formalReport && selectedObservation && (
        <section id="formal-red-zone-report" className={styles.middleContainer}>
          <SectionHeader
            danger
            icon={<BsShieldFillExclamation />}
            title="Formal Red Zone Report"
            subtitle="Escalate the selected map observation into a complete incident record."
          />

          <div className={styles.formalSourceBanner}>
            <BsGeoAltFill />
            <div>
              <strong>Source Observation</strong>
              <span>
                {getLocationLabel(selectedObservation)} •{' '}
                {formatObservationTime(
                  selectedObservation.observedAt,
                  timezone
                )}
              </span>
            </div>
          </div>

          <div className={styles.reportForm}>
            <Field label="Date / Time Observed" required>
              <input
                type="datetime-local"
                value={formalReport.observedAt}
                onChange={(event) =>
                  setFormalReport((previous) =>
                    previous
                      ? { ...previous, observedAt: event.target.value }
                      : previous
                  )
                }
              />
            </Field>

            <Field label="Protector Name" required>
              <select
                value={formalReport.protectorId}
                onChange={(event) =>
                  setFormalReport((previous) =>
                    previous
                      ? { ...previous, protectorId: event.target.value }
                      : previous
                  )
                }
              >
                <option value="">Select protector</option>
                {protectorOptions.map((protector) => (
                  <option key={protector.id} value={protector.id}>
                    {protector.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Map Location" required>
              <input readOnly value={getLocationLabel(selectedObservation)} />
            </Field>

            <Field label="Direction of Travel">
              <select
                value={formalReport.direction}
                onChange={(event) =>
                  setFormalReport((previous) =>
                    previous
                      ? {
                          ...previous,
                          direction: event.target.value as
                            RedZoneTravelDirection | '',
                        }
                      : previous
                  )
                }
              >
                <option value="">Select direction</option>
                {DIRECTIONS.map((direction) => (
                  <option key={direction} value={direction}>
                    {direction}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Incident Type" required>
              <select
                value={formalReport.incidentType}
                onChange={(event) =>
                  setFormalReport((previous) =>
                    previous
                      ? {
                          ...previous,
                          incidentType: event.target.value as
                            RedZoneIncidentType | '',
                        }
                      : previous
                  )
                }
              >
                <option value="">Select incident type</option>
                {RED_ZONE_INCIDENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Risk Level / Priority" required>
              <select
                value={formalReport.riskLevel}
                onChange={(event) =>
                  setFormalReport((previous) =>
                    previous
                      ? {
                          ...previous,
                          riskLevel: event.target.value as
                            RedZoneRiskLevel | '',
                        }
                      : previous
                  )
                }
              >
                <option value="">Select risk</option>
                {RISK_LEVELS.map((risk) => (
                  <option key={risk} value={risk}>
                    {risk}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Narrative / Observations"
              required
              className={styles.fullWidth}
            >
              <textarea
                rows={5}
                value={formalReport.narrative}
                placeholder="Describe what was observed, why it matters, movement, behavior, identifying details and any action taken."
                onChange={(event) =>
                  setFormalReport((previous) =>
                    previous
                      ? { ...previous, narrative: event.target.value }
                      : previous
                  )
                }
              />
            </Field>
          </div>

          <div className={styles.detailSectionsGrid}>
            <div className={styles.entityDetailsCard}>
              <SectionHeader
                icon={<BsCarFrontFill />}
                title="Vehicle Details"
              />
              <div className={styles.vehicleGrid}>
                {VEHICLE_FIELDS.map((field) => (
                  <Field
                    key={field.key}
                    label={field.label}
                    className={field.wide ? styles.spanTwo : undefined}
                  >
                    <input
                      value={String(formalReport.vehicle[field.key] ?? '')}
                      onChange={(event) =>
                        updateFormalVehicle(field.key, event.target.value)
                      }
                    />
                  </Field>
                ))}
                <UploadBox
                  label="Upload Vehicle Photo"
                  file={formalReport.vehiclePhoto}
                  onChange={(file) =>
                    setFormalReport((previous) =>
                      previous ? { ...previous, vehiclePhoto: file } : previous
                    )
                  }
                />
              </div>
            </div>

            <div className={styles.entityDetailsCard}>
              <SectionHeader icon={<BsPersonFill />} title="Person Details" />
              <div className={styles.personGrid}>
                {PERSON_FIELDS.map((field) => (
                  <Field
                    key={field.key}
                    label={field.label}
                    className={field.wide ? styles.spanTwo : undefined}
                  >
                    <input
                      value={String(formalReport.person[field.key] ?? '')}
                      onChange={(event) =>
                        updateFormalPerson(field.key, event.target.value)
                      }
                    />
                  </Field>
                ))}
                <UploadBox
                  label="Upload Person Photo"
                  file={formalReport.personPhoto}
                  onChange={(file) =>
                    setFormalReport((previous) =>
                      previous ? { ...previous, personPhoto: file } : previous
                    )
                  }
                />
              </div>
            </div>
          </div>

          <div className={styles.actionBar}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => setFormalReport(null)}
              disabled={savingReport}
            >
              Close
            </button>
            <button
              type="button"
              className={styles.saveButton}
              onClick={() => submitFormalReport('DRAFT')}
              disabled={savingReport}
            >
              <BsBookmark /> Save Draft
            </button>
            <button
              type="button"
              className={styles.escalateButton}
              onClick={() => submitFormalReport('ESCALATED')}
              disabled={savingReport}
            >
              <BsExclamationTriangle /> Escalate
            </button>
            <button
              type="button"
              className={styles.submitButton}
              onClick={() => submitFormalReport('SUBMITTED')}
              disabled={savingReport}
            >
              <BsSendFill /> {savingReport ? 'Saving…' : 'Submit Report'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default RedZone;
